import { Copy, Globe2, Info, Lightbulb, PenLine } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScoreRing } from "@/components/ScoreRing";
import { dimensionKey, useI18n } from "@/i18n";
import { BAND_CHIP_CLASS, DIMENSION_WEIGHTS, scoreBand } from "@/lib/score";

const IMPACT_CLASS = {
  high: "bg-destructive/10 text-destructive border-destructive/25",
  medium: "bg-warning-light text-warning border-warning/25",
  low: "bg-muted text-muted-foreground border-border",
};

function ImpactBadge({ impact, label }) {
  const key = (impact || "").toLowerCase();
  const tone = IMPACT_CLASS[key] || IMPACT_CLASS.low;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tone}`}
    >
      {label}
    </span>
  );
}

export const ReportSection = ({ review, onCopy }) => {
  const { t } = useI18n();
  const dimensions = review?.dimensions || [];
  const recommendations = review?.priority_recommendations || [];
  const excerpts = review?.revised_excerpts || [];
  const assumptions = review?.assumptions || [];
  const marketNotes = review?.market_notes || [];

  const translateDimension = (name) => {
    const key = dimensionKey(name);
    return key ? t(key) : name;
  };

  const translateImpact = (impact) => {
    const key = (impact || "").toLowerCase();
    if (["high", "medium", "low"].includes(key)) return t(`report.impact.${key}`);
    return impact || t("report.impact.fallback");
  };

  return (
    <div className="space-y-6">
      {/* ── Dimensions ── */}
      {!!dimensions.length && (
        <section className="space-y-4" data-testid="dimensions-card">
          <h3 className="font-heading text-xl font-semibold tracking-tight">
            {t("report.dimensionsTitle")}
          </h3>
          <div className="stagger-children space-y-4" data-testid="dimension-accordion">
            {dimensions.map((dim) => (
              <Card
                className="surface-card border-border"
                key={dim.dimension}
                data-testid={`dimension-card-${slug(dim.dimension)}`}
              >
                <CardContent className="space-y-4 p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                    <ScoreRing score={dim.score} size="small" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                        <h4 className="font-heading text-base font-semibold">
                          {translateDimension(dim.dimension)}
                        </h4>
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                            BAND_CHIP_CLASS[scoreBand(dim.score)]
                          }`}
                        >
                          {t(`score.band.${scoreBand(dim.score)}`)}
                        </span>
                        {DIMENSION_WEIGHTS[dim.dimension] != null && (
                          <span className="text-xs text-muted-foreground">
                            {t("report.weightShort", {
                              n: Math.round(DIMENSION_WEIGHTS[dim.dimension] * 100),
                            })}
                          </span>
                        )}
                      </div>
                      {dim.observations && (
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          {dim.observations}
                        </p>
                      )}
                    </div>
                  </div>
                  {dim.score_rationale && (
                    <p
                      className="rounded-lg border-l-2 border-primary/50 bg-muted/50 px-3.5 py-2.5 text-sm leading-6 text-foreground/85"
                      data-testid={`dimension-score-rationale-${slug(dim.dimension)}`}
                    >
                      <strong className="font-semibold">
                        {t("report.scoreRationaleLabel")}:
                      </strong>{" "}
                      {dim.score_rationale}
                    </p>
                  )}
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FeedbackPanel
                      variant="positive"
                      title={t("report.strengths")}
                      items={dim.strengths}
                      emptyLabel={t("report.noNotes")}
                    />
                    <FeedbackPanel
                      variant="attention"
                      title={t("report.improvements")}
                      items={dim.improvements}
                      emptyLabel={t("report.noNotes")}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* ── Priority recommendations ── */}
      {!!recommendations.length && (
        <section className="space-y-4" data-testid="priority-recommendations-card">
          <h3 className="font-heading flex items-center gap-2 text-xl font-semibold tracking-tight">
            <Lightbulb className="h-5 w-5 text-accent-strong" aria-hidden="true" />
            {t("report.priorityRec")}
          </h3>
          <div className="space-y-4">
            {recommendations
              .slice()
              .sort((a, b) => (a.rank || 99) - (b.rank || 99))
              .map((rec, index) => (
                <Card
                  className="surface-card border-l-4 border-border border-l-primary"
                  key={`${rec.title}-${index}`}
                  data-testid={`priority-recommendation-${rec.rank || index + 1}`}
                >
                  <CardContent className="space-y-3 p-5 sm:p-6">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary font-heading text-xs font-bold text-primary-foreground">
                        {rec.rank || index + 1}
                      </span>
                      <h4 className="font-heading min-w-0 flex-1 text-base font-semibold">
                        {rec.title}
                      </h4>
                      <ImpactBadge impact={rec.impact} label={translateImpact(rec.impact)} />
                    </div>
                    {rec.rationale && (
                      <p className="text-sm leading-7 text-foreground/85">{rec.rationale}</p>
                    )}
                    {rec.example && (
                      <div className="rounded-lg border border-border bg-muted/50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {t("report.exampleRewrite")}
                        </p>
                        <p className="mt-1.5 text-sm leading-7">{rec.example}</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="no-print mt-3 gap-1.5 bg-card"
                          onClick={() => onCopy?.(rec.example)}
                          data-testid={`recommendation-copy-button-${rec.rank || index + 1}`}
                        >
                          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                          {t("report.copyExample")}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
          </div>
        </section>
      )}

      {/* ── Revised excerpts ── */}
      {!!excerpts.length && (
        <section className="space-y-4" data-testid="revised-excerpts-card">
          <h3 className="font-heading flex items-center gap-2 text-xl font-semibold tracking-tight">
            <PenLine className="h-5 w-5 text-primary" aria-hidden="true" />
            {t("report.revisedExcerpts")}
          </h3>
          <div className="space-y-4">
            {excerpts.map((ex, index) => (
              <Card
                className="surface-card border-border"
                key={`${ex.section}-${index}`}
                data-testid={`revised-excerpt-${index + 1}`}
              >
                <CardContent className="space-y-3 p-5 sm:p-6">
                  {ex.section && (
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {ex.section}
                    </p>
                  )}
                  {ex.original && (
                    <div className="excerpt-quote excerpt-quote--original">
                      <p className="text-xs font-semibold text-destructive">
                        {t("report.original")}
                      </p>
                      <p className="mt-1 text-sm leading-7 text-foreground/85">{ex.original}</p>
                    </div>
                  )}
                  <div className="excerpt-quote excerpt-quote--revised">
                    <p className="text-xs font-semibold text-success">{t("report.revised")}</p>
                    <p className="mt-1 text-sm leading-7">{ex.revised}</p>
                  </div>
                  {ex.why_it_works && (
                    <p className="text-sm leading-6 text-muted-foreground">
                      <strong className="font-semibold text-foreground/80">
                        {t("report.whyItWorks")}:
                      </strong>{" "}
                      {ex.why_it_works}
                    </p>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="no-print gap-1.5 bg-card"
                    onClick={() => onCopy?.(ex.revised)}
                    data-testid={`excerpt-copy-button-${index + 1}`}
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("report.copyRevised")}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* ── Market notes + assumptions ── */}
      {(!!marketNotes.length || !!assumptions.length) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {!!marketNotes.length && (
            <Card className="surface-card border-border" data-testid="market-notes-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading flex items-center gap-2 text-base">
                  <Globe2 className="h-4 w-4 text-primary" aria-hidden="true" />
                  {t("report.marketNotes")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FeedbackList items={marketNotes} emptyLabel={t("report.noNotes")} />
              </CardContent>
            </Card>
          )}
          {!!assumptions.length && (
            <Card className="surface-card border-border" data-testid="assumptions-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading flex items-center gap-2 text-base">
                  <Info className="h-4 w-4 text-primary" aria-hidden="true" />
                  {t("report.assumptions")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FeedbackList items={assumptions} emptyLabel={t("report.noNotes")} />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

function FeedbackPanel({ variant, title, items = [], emptyLabel }) {
  const isPositive = variant === "positive";
  return (
    <div className={`feedback-panel ${isPositive ? "feedback-panel--positive" : "feedback-panel--attention"}`}>
      <p className={`feedback-panel-title ${isPositive ? "text-success" : "text-warning"}`}>
        {title}
      </p>
      <FeedbackList items={items} emptyLabel={emptyLabel} />
    </div>
  );
}

function FeedbackList({ items = [], emptyLabel = "No notes provided." }) {
  if (!items.length) return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  return (
    <ul className="space-y-2" data-testid="feedback-list">
      {items.map((item, index) => (
        <li className="flex min-w-0 gap-2.5 text-sm leading-6" key={`${item}-${index}`}>
          <span className="bullet-dot" aria-hidden="true" />
          <span className="min-w-0">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function slug(value) {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
